import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Chip,
  Autocomplete,
  Alert,
  CircularProgress,
  Switch,
} from "@mui/material";
import {
  PhotoCamera,
  Delete,
  CheckCircle,
  ArrowBack,
  ArrowForward,
} from "@mui/icons-material";
import { uploadVideoMultipart } from "./helpers/videoUpload";
import { useParams, useNavigate } from "react-router-dom";

const initialProperty = {
  // type selection
  listingFor: "selling", // selling | rental
  propertyType: "plot", // depends on listingFor

  // common fields
  name: "",
  city: "",
  plotArea: "",
  plotUnit: "sqft",
  builtUp: "",
  carpetArea: "",
  superBuiltUp: "",
  priceUnit: "Cr",
  direction: "",
  buildingYear: "",
  homeLoan: false,
  rccType: "",
  naProperty: false,
  rate: "",
  floor: "",
  resale: false,
  location: "",
  imageIds: [],
  videoId: null,
  nearBy: [],
  description: "",
  // rental specific
  rent: "",
  deposit: "",
  // extra
  negotiable: false,
  lift: false,
  parking: false,
  rentUnit: "Thousands",
  // bhk/rk
  bhkType: "", // BHK or RK
  bhkCount: 0,
};
const priceUnits = ["Crores", "Lakhs", "Thousands"];

const highlightsOptions = [
  "Global School",
  "Swimming Pool",
  "Shopping Mall",
  "Hospital",
  "Park",
  "Metro Station",
];

const LS_KEY = "add_property_draft_v1";

const API_URL = window.location.href.includes("localhost")
  ? process.env.REACT_APP_API_URL_LOCAL
  : process.env.REACT_APP_API_URL_PROD;

export default function AddSitesMultiStep() {
  const { id: propertyId } = useParams();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Property Type", "Details", "Images & Highlights", "Video & Submit"]; // success handled as step index 4
  const [property, setProperty] = useState(initialProperty);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [continueDisabled, setContinueDisabled] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imagesUploaded, setImagesUploaded] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoUploaded, setVideoUploaded] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [savedPropertyName, setSavedPropertyName] = useState("");


  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch(`${API_URL}api/get-property/${propertyId}`);
        const json = await res.json();
        if (json.success) {
          setProperty(json.data);
          setImagePreviews(
            json.data.imageIds?.map((id) => `https://skyline-properties-media.s3.ap-south-1.amazonaws.com/${id}`) || []
          );
        } else {
          console.error("Error:", json.message);
        }
      } catch (err) {
        console.error("Error loading property", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [propertyId]);



  useEffect(() => {
    // load draft
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setProperty((p) => ({ ...p, ...parsed.property }));
        if (parsed.imagePreviews && Array.isArray(parsed.imagePreviews)) setImagePreviews(parsed.imagePreviews);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(""), 3500);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  useEffect(() => {
    if (errorMsg) {
      const t = setTimeout(() => setErrorMsg(""), 3500);
      return () => clearTimeout(t);
    }
  }, [errorMsg]);

  const update = (patch) => setProperty((p) => ({ ...p, ...patch }));

  const saveDraft = () => {
    try {
      const payload = { property, imagePreviews };
      localStorage.setItem(LS_KEY, JSON.stringify(payload));
      setSuccessMsg("Draft saved to browser");
    } catch (e) {
      setErrorMsg("Unable to save draft");
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(LS_KEY);
  };

  // --- File helpers (same API contract your backend already uses) ---
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API_URL}api/upload`, { method: "POST", body: fd });
    if (!res.ok) throw new Error("Upload failed");
    const json = await res.json();
    if (json.success && json.uploaded?.length > 0) return json.uploaded[0];
    throw new Error("Upload failed");
  };

  const uploadImages = async () => {
    if (!imageFiles.length) throw new Error("No images to upload");
    setUploadingImages(true);
    try {
      const ids = [];
      for (let i = 0; i < imageFiles.length; i++) {
        const key = await uploadToS3(imageFiles[i]); // ✅ presigned upload
        ids.push(key);
      }
      update({ imageIds: ids });
      setImagesUploaded(true);
      setSuccessMsg(`${ids.length} image${ids.length > 1 ? "s" : ""} uploaded`);
      return ids;
    } finally {
      setUploadingImages(false);
    }
  };


  const getPresignedUrl = async (file) => {
    const res = await fetch(
      `${API_URL}api/presign?filename=${encodeURIComponent(file.name)}`
    );
    return res.json();
  };
  const uploadVideo = async (file, onProgress) => {
    return uploadToS3(file, onProgress); // ✅ returns video S3 key
  };




  const uploadToS3 = async (file, onProgress) => {
    const { url, key } = await getPresignedUrl(file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve(key); // ✅ return only S3 object key
        } else {
          console.error("S3 Upload Error:", xhr.status, xhr.responseText);
          reject(new Error(`Upload failed: ${xhr.status} - ${xhr.responseText}`));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.send(file);
    });
  };




  // --- validations per step ---
  // Requirement: remove most validations. Only images are required before submit.
  const validateStepImages = () => {
    if (!imageFiles.length && !imagePreviews.length) {
      setErrorMsg("Please add at least one image");
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    try {
      if (activeStep === 0) {
        // user selected listingFor and propertyType
        setActiveStep(1);
        return;
      }
      if (activeStep === 1) {
        setActiveStep(2);
        return;
      }
      if (activeStep === 2) {
        if (!validateStepImages()) return;
        // upload images if files exist and not uploaded yet
        if (imageFiles.length && !imagesUploaded) {
          try {
            await uploadImages();
          } catch {
            setErrorMsg("Image upload failed — try again");
            return;
          }
        }
        setActiveStep(3);
        return;
      }
      if (activeStep === 3) {
        try {
          setContinueDisabled(true);

          let videoKey = property.videoId; // keep existing if already uploaded

          if (videoFile && !videoUploaded) {
            setUploadingVideo(true);
            videoKey = await uploadVideoMultipart(videoFile, API_URL, setVideoProgress);
            update({ videoId: videoKey });
            setVideoUploaded(true);
          }

          await handleSubmit(videoKey); // submit even if no video
        } catch (err) {
          console.error(err);
          setErrorMsg("Video upload failed");
        } finally {
          setUploadingVideo(false);
          setContinueDisabled(false);
        }
      }





    } catch (err) {
      console.error(err);
      setErrorMsg("Something went wrong — try again");
    }
  };

  const handleSubmit = async (videoId) => {
    setSavingProperty(true);
    try {
      const payload = { ...property, videoId, created_by: "ADMIN" };
      const res = await fetch(`${API_URL}api/properties/${propertyId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Save failed");

      const json = await res.json();
      if (json.success) {
        setSavedPropertyName(property.name || `${property.propertyType}`);
        setActiveStep(4);
        clearDraft();
      } else {
        setErrorMsg(json.message || "Save failed");
      }
    } catch (e) {
      console.error(e);
      setErrorMsg("Error saving property");
    } finally {
      setSavingProperty(false);
    }
  };

  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const onSelectImages = async (files) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;
    const newPreviews = await Promise.all(
      selected.map(
        (f) =>
          new Promise((res) => {
            const reader = new FileReader();
            reader.onload = () => res(reader.result);
            reader.readAsDataURL(f);
          })
      )
    );
    setImageFiles((prev) => [...prev, ...selected]);
    setImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
    update({ imageIds: property.imageIds.filter((_, i) => i !== index) });
  };

  // Helpers for showing fields based on selection
  const isSelling = property.listingFor === "selling";
  const isRental = property.listingFor === "rental";
  const pt = property.propertyType;

  // Render field sets per requirement
  const renderFieldsForType = () => {
    switch (pt) {
      case "plot":
        return (
          <>
            <TextField label="Property Name" value={property.name} onChange={(e) => update({ name: e.target.value })} fullWidth />
            <TextField label="City" value={property.city} onChange={(e) => update({ city: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Plot Area" value={property.plotArea} onChange={(e) => update({ plotArea: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Direction" value={property.direction} onChange={(e) => update({ direction: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <FormControlLabel control={<Checkbox checked={property.homeLoan} onChange={(e) => update({ homeLoan: e.target.checked })} />} label="Home loan available" sx={{ mt: 1 }} />
            <FormControlLabel control={<Checkbox checked={property.naProperty} onChange={(e) => update({ naProperty: e.target.checked })} />} label="NA Property" sx={{ mt: 1 }} />

            {/* <TextField label={isRental ? "Monthly Rent" : "Price"} value={isRental ? property.rent : property.rate} onChange={(e) => update(isRental ? { rent: e.target.value } : { rate: e.target.value })} fullWidth sx={{ mt: 2 }} /> */}
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                label={isRental ? "Monthly Rent" : "Price"}
                value={isRental ? property.rent : property.rate}
                onChange={(e) =>
                  update(isRental ? { rent: e.target.value } : { rate: e.target.value })
                }
                fullWidth
              />

              <Select
                value={isRental ? property.rentUnit : property.priceUnit}
                onChange={(e) =>
                  update(isRental ? { rentUnit: e.target.value } : { priceUnit: e.target.value })
                }
                sx={{ minWidth: 120 }}
              >
                {priceUnits.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
            </Box>


            {isRental && <TextField label="Deposit" value={property.deposit} onChange={(e) => update({ deposit: e.target.value })} fullWidth sx={{ mt: 2 }} />}
            <FormControlLabel control={<Checkbox checked={property.resale} onChange={(e) => update({ resale: e.target.checked })} />} label="Resale" sx={{ mt: 1 }} />
            <TextField label="Address" value={property.location} onChange={(e) => update({ location: e.target.value })} fullWidth multiline rows={2} sx={{ mt: 2 }} />
          </>
        );

      case "flat":
      case "shop":
        return (
          <>
            <TextField label={pt === "flat" ? "Apartment Name" : "Shop Name"} value={property.name} onChange={(e) => update({ name: e.target.value })} fullWidth />
            {pt === "flat" && (
              <TextField label="Floor" value={property.floor || ""} onChange={(e) => update({ floor: e.target.value })} fullWidth sx={{ mt: 2 }} />
            )}
            <TextField label="Building Year" value={property.buildingYear} onChange={(e) => update({ buildingYear: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="City" value={property.city} onChange={(e) => update({ city: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Carpet Area" value={property.carpetArea} onChange={(e) => update({ carpetArea: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Super Built Up" value={property.superBuiltUp} onChange={(e) => update({ superBuiltUp: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Direction" value={property.direction} onChange={(e) => update({ direction: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <FormControlLabel control={<Checkbox checked={property.homeLoan} onChange={(e) => update({ homeLoan: e.target.checked })} />} label="Home loan available" sx={{ mt: 1 }} />
            <FormControlLabel control={<Checkbox checked={property.naProperty} onChange={(e) => update({ naProperty: e.target.checked })} />} label="NA Property" sx={{ mt: 1 }} />
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                label={isRental ? "Monthly Rent" : "Price"}
                value={isRental ? property.rent : property.rate}
                onChange={(e) =>
                  update(isRental ? { rent: e.target.value } : { rate: e.target.value })
                }
                fullWidth
              />

              <Select
                value={isRental ? property.rentUnit : property.priceUnit}
                onChange={(e) =>
                  update(isRental ? { rentUnit: e.target.value } : { priceUnit: e.target.value })
                }
                sx={{ minWidth: 120 }}
              >
                {priceUnits.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {isRental && <TextField label="Deposit" value={property.deposit} onChange={(e) => update({ deposit: e.target.value })} fullWidth sx={{ mt: 2 }} />}
            <FormControlLabel control={<Checkbox checked={property.resale} onChange={(e) => update({ resale: e.target.checked })} />} label="Resale" sx={{ mt: 1 }} />
            <FormControlLabel control={<Checkbox checked={property.lift} onChange={(e) => update({ lift: e.target.checked })} />} label="Lift" sx={{ mt: 1 }} />
            <FormControlLabel control={<Checkbox checked={property.parking} onChange={(e) => update({ parking: e.target.checked })} />} label="Parking" sx={{ mt: 1 }} />
            <TextField label="Address" value={property.location} onChange={(e) => update({ location: e.target.value })} fullWidth multiline rows={2} sx={{ mt: 2 }} />
          </>
        );

      case "bungalow":
        return (
          <>
            <TextField label="Bungalow Name" value={property.name} onChange={(e) => update({ name: e.target.value })} fullWidth />
            <TextField label="City" value={property.city} onChange={(e) => update({ city: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Built Up" value={property.builtUp} onChange={(e) => update({ builtUp: e.target.value })} fullWidth sx={{ mt: 2 }} />
            {/* ✅ Added Plot Area */}
            <TextField
              label="Plot Area"
              value={property.plotArea}
              onChange={(e) => update({ plotArea: e.target.value })}
              fullWidth
              sx={{ mt: 2 }}
            />
            <TextField label="Direction" value={property.direction} onChange={(e) => update({ direction: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <FormControlLabel control={<Checkbox checked={property.homeLoan} onChange={(e) => update({ homeLoan: e.target.checked })} />} label="Home loan available" sx={{ mt: 1 }} />
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                label={isRental ? "Monthly Rent" : "Price"}
                value={isRental ? property.rent : property.rate}
                onChange={(e) =>
                  update(isRental ? { rent: e.target.value } : { rate: e.target.value })
                }
                fullWidth
              />

              <Select
                value={isRental ? property.rentUnit : property.priceUnit}
                onChange={(e) =>
                  update(isRental ? { rentUnit: e.target.value } : { priceUnit: e.target.value })
                }
                sx={{ minWidth: 120 }}
              >
                {priceUnits.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {isRental && <TextField label="Deposit" value={property.deposit} onChange={(e) => update({ deposit: e.target.value })} fullWidth sx={{ mt: 2 }} />}
            <FormControlLabel control={<Checkbox checked={property.resale} onChange={(e) => update({ resale: e.target.checked })} />} label="Resale" sx={{ mt: 1 }} />
            <TextField label="Address" value={property.location} onChange={(e) => update({ location: e.target.value })} fullWidth multiline rows={2} sx={{ mt: 2 }} />
          </>
        );

      case "rowhouse":
        return (
          <>
            <TextField label="Rowhouse Name" value={property.name} onChange={(e) => update({ name: e.target.value })} fullWidth />
            <TextField label="City" value={property.city} onChange={(e) => update({ city: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Built Up" value={property.builtUp} onChange={(e) => update({ builtUp: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Direction" value={property.direction} onChange={(e) => update({ direction: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <FormControlLabel control={<Checkbox checked={property.homeLoan} onChange={(e) => update({ homeLoan: e.target.checked })} />} label="Home loan available" sx={{ mt: 1 }} />
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                label={isRental ? "Monthly Rent" : "Price"}
                value={isRental ? property.rent : property.rate}
                onChange={(e) =>
                  update(isRental ? { rent: e.target.value } : { rate: e.target.value })
                }
                fullWidth
              />

              <Select
                value={isRental ? property.rentUnit : property.priceUnit}
                onChange={(e) =>
                  update(isRental ? { rentUnit: e.target.value } : { priceUnit: e.target.value })
                }
                sx={{ minWidth: 120 }}
              >
                {priceUnits.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {isRental && <TextField label="Deposit" value={property.deposit} onChange={(e) => update({ deposit: e.target.value })} fullWidth sx={{ mt: 2 }} />}
            <FormControlLabel control={<Checkbox checked={property.resale} onChange={(e) => update({ resale: e.target.checked })} />} label="Resale" sx={{ mt: 1 }} />
            <TextField label="Address" value={property.location} onChange={(e) => update({ location: e.target.value })} fullWidth multiline rows={2} sx={{ mt: 2 }} />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: "80vh", py: 6, px: 2, background: "linear-gradient(180deg,#0f172a 0%, #0ea5a4 100%)", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
      <Box sx={{ width: "100%", maxWidth: 1000, bgcolor: "background.paper", borderRadius: 3, p: { xs: 2, md: 4 }, boxShadow: 12, position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", top: -120, right: -120, width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle at 30% 20%, rgba(99,102,241,0.9), transparent 30%), radial-gradient(circle at 80% 80%, rgba(236,72,153,0.6), transparent 30%)", filter: "blur(40px)", zIndex: 0 }} />

        <Box sx={{ position: "relative", zIndex: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: "primary.dark" }}>Add New Property</Typography>

          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 0: Property Type */}
          {activeStep === 0 && (
            <Box>
              <Typography sx={{ mb: 2, fontWeight: 600 }}>Listing For</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Button variant={property.listingFor === "selling" ? "contained" : "outlined"} fullWidth onClick={() => update({ listingFor: "selling", propertyType: "plot" })}>
                    Selling
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button variant={property.listingFor === "rental" ? "contained" : "outlined"} fullWidth onClick={() => update({ listingFor: "rental", propertyType: "shop" })}>
                    Rental
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <Typography sx={{ mt: 1 }}>Choose Property Sub-type</Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                    {(property.listingFor === "selling" ? ["plot", "flat", "bungalow", "rowhouse", "shop"] : ["shop", "flat", "bungalow"]).map((ptype) => (
                      <Button key={ptype} variant={property.propertyType === ptype ? "contained" : "outlined"} onClick={() => update({ propertyType: ptype })}>
                        {ptype.charAt(0).toUpperCase() + ptype.slice(1)}
                      </Button>
                    ))}
                  </Box>
                </Grid>

                <Box
                  sx={{
                    mt: 2,
                    ml: 2,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" }, // stack on mobile, row on desktop
                    alignItems: "stretch",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <Button
                    onClick={handleBack}
                    startIcon={<ArrowBack />}
                    variant="outlined"
                    fullWidth={{ xs: true, sm: false }} // full width only on mobile
                  >
                    Back
                  </Button>

                  <Button
                    onClick={saveDraft}
                    variant="outlined"
                    fullWidth={{ xs: true, sm: false }}
                  >
                    Save Draft
                  </Button>

                  <Button
                    onClick={handleNext}
                    variant="contained"
                    endIcon={<ArrowForward />}
                    fullWidth={{ xs: true, sm: false }}
                    disabled={uploadingImages}
                    sx={{
                      ml: { sm: "auto" }, // only push on desktop
                      background: "linear-gradient(90deg,#06b6d4,#7c3aed)",
                      color: "white",
                    }}
                  >
                    Continue
                  </Button>
                </Box>



              </Grid>
            </Box>
          )}

          {/* Step 1: Details (dynamic) */}
          {activeStep === 1 && (
            <Box component="form" noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  {renderFieldsForType()}

                  {/* BHK / RK selection available for flats and some residential types */}
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" }, // stack on mobile
                      gap: 2,
                      alignItems: { xs: "flex-start", sm: "center" },
                      width: "100%",
                    }}
                  >
                    <Select
                      value={property.bhkType}
                      onChange={(e) => update({ bhkType: e.target.value })}
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value="BHK">BHK</MenuItem>
                      <MenuItem value="RK">RK</MenuItem>
                    </Select>

                    <Select
                      value={property.bhkCount}
                      onChange={(e) => update({ bhkCount: e.target.value })}
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                      <MenuItem value={3}>3</MenuItem>
                      <MenuItem value={4}>4</MenuItem>
                      <MenuItem value={5}>5</MenuItem>
                    </Select>


                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: { xs: 1, sm: 0 }, // push down only on mobile
                      }}
                    >
                      <Typography>Negotiable</Typography>
                      <Switch
                        checked={property.negotiable}
                        onChange={(e) => update({ negotiable: e.target.checked })}
                      />
                    </Box>
                  </Box>

                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => update({ bhkType: "", bhkCount: 0 })}
                  >
                    Remove BHK/RK
                  </Button>


                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" }, // stack on mobile, row on desktop
                      alignItems: "stretch",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <Button
                      onClick={handleBack}
                      startIcon={<ArrowBack />}
                      variant="outlined"
                      fullWidth={{ xs: true, sm: false }} // full width only on mobile
                    >
                      Back
                    </Button>

                    <Button
                      onClick={saveDraft}
                      variant="outlined"
                      fullWidth={{ xs: true, sm: false }}
                    >
                      Save Draft
                    </Button>

                    <Button
                      onClick={handleNext}
                      variant="contained"
                      endIcon={<ArrowForward />}
                      fullWidth={{ xs: true, sm: false }}
                      disabled={uploadingImages}
                      sx={{
                        ml: { sm: "auto" }, // only push on desktop
                        background: "linear-gradient(90deg,#06b6d4,#7c3aed)",
                        color: "white",
                      }}
                    >
                      Continue
                    </Button>
                  </Box>


                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography sx={{ mb: 1 }}>Tips</Typography>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: "background.default", boxShadow: 1 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>Suggestions</Typography>
                    <ul style={{ marginTop: 0 }}>
                      <li>Fill important fields — but you can skip and come back later using Save Draft.</li>
                      <li>Images are required before submission.</li>
                    </ul>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 2: Images & Highlights */}
          {activeStep === 2 && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ mb: 1 }}>Upload images</Typography>
                  <input accept="image/*" id="image-upload" type="file" multiple style={{ display: "none" }} onChange={(e) => onSelectImages(e.target.files)} />
                  <label htmlFor="image-upload">
                    <Button variant="contained" component="span" startIcon={<PhotoCamera />} sx={{ mb: 2 }}>Add Images</Button>
                  </label>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {imagePreviews.length ? imagePreviews.map((src, i) => (
                      <Box key={i} sx={{ position: "relative", width: 120, height: 80, borderRadius: 2, overflow: "hidden", boxShadow: 3 }}>
                        <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        {uploadingImages ? <CircularProgress size={28} sx={{ position: "absolute", top: "35%", left: "35%" }} /> :
                          <CheckCircle color={imagesUploaded ? "success" : "disabled"} sx={{ position: "absolute", top: 6, left: 6 }} />}
                        <IconButton size="small" onClick={() => removeImage(i)} sx={{ position: "absolute", top: 6, right: 6, bgcolor: "rgba(255,255,255,0.7)" }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    )) : <Typography sx={{ color: "text.secondary" }}>No images selected</Typography>}
                  </Box>

                  <Autocomplete multiple freeSolo options={highlightsOptions} value={property.nearBy} onChange={(e, val) => update({ nearBy: val })} renderTags={(value, getTagProps) => value.map((option, index) => (<Chip key={index} label={option} {...getTagProps({ index })} />))} renderInput={(params) => <TextField {...params} label="Nearby Highlights" placeholder="Add nearby highlights" sx={{ mt: 2 }} />} />

                  <TextField label="Property Description" value={property.description} onChange={(e) => { const words = e.target.value.split(/\s+/).slice(0, 300).join(" "); update({ description: words }); }} fullWidth multiline rows={4} sx={{ mt: 2 }} helperText={`${property.description.split(/\s+/).filter(Boolean).length} / 300 words`} />

                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" }, // stack on mobile, row on desktop
                      alignItems: "stretch",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <Button
                      onClick={handleBack}
                      startIcon={<ArrowBack />}
                      variant="outlined"
                      fullWidth={{ xs: true, sm: false }} // full width only on mobile
                    >
                      Back
                    </Button>

                    <Button
                      onClick={saveDraft}
                      variant="outlined"
                      fullWidth={{ xs: true, sm: false }}
                    >
                      Save Draft
                    </Button>

                    <Button
                      onClick={handleNext}
                      variant="contained"
                      endIcon={<ArrowForward />}
                      fullWidth={{ xs: true, sm: false }}
                      disabled={uploadingImages}
                      sx={{
                        ml: { sm: "auto" }, // only push on desktop
                        background: "linear-gradient(90deg,#06b6d4,#7c3aed)",
                        color: "white",
                      }}
                    >
                      Continue
                    </Button>
                  </Box>



                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ mb: 1 }}>Preview & Tips</Typography>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: "background.default", boxShadow: 1 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>Tips</Typography>
                    <ul style={{ marginTop: 0 }}>
                      <li>Make sure images are clear and well-lit.</li>
                      <li>Use different angles to show the property.</li>
                      <li>JPG/PNG only.</li>
                    </ul>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 3: Video & Submit */}
          {activeStep === 3 && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ mb: 1 }}>Upload a short video (optional)</Typography>
                  <input id="video-upload" accept="video/*" type="file" style={{ display: "none" }} onChange={(e) => setVideoFile(e.target.files[0])} />
                  <label htmlFor="video-upload">
                    <Button variant="contained" component="span" startIcon={<PhotoCamera />} sx={{ mb: 2 }}>Select Video</Button>
                  </label>

                  {videoFile && (
                    <Box sx={{ mt: 1 }}>
                      {/* Video Preview */}
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          maxWidth: 400,
                          borderRadius: 2,
                          overflow: "hidden",
                          boxShadow: 3,
                          mb: 1.5,
                        }}
                      >
                        <video
                          controls
                          style={{ width: "100%", borderRadius: 8 }}
                          src={URL.createObjectURL(videoFile)}
                        />
                      </Box>

                      {/* File name */}
                      <Typography sx={{ fontSize: 14 }}>{videoFile.name}</Typography>

                      {/* Linear Progress */}
                      {uploadingVideo && (
                        <LinearProgress
                          variant="determinate"
                          value={videoProgress}
                          sx={{ mt: 1, height: 8, borderRadius: 5 }}
                        />
                      )}

                      {/* Circular + % or Uploaded */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mt: 1.5,
                        }}
                      >
                        {uploadingVideo && (
                          <>
                            <CircularProgress size={16} thickness={4} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {videoProgress}%
                            </Typography>
                          </>
                        )}

                        {!uploadingVideo && videoUploaded && (
                          <Typography
                            variant="body2"
                            color="success.main"
                            sx={{ fontWeight: 600 }}
                          >
                            ✅ Uploaded
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" }, // stack on mobile, row on desktop
                      alignItems: "stretch",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <Button
                      onClick={handleBack}
                      startIcon={<ArrowBack />}
                      variant="outlined"
                      fullWidth={{ xs: true, sm: false }} // full width only on mobile
                    >
                      Back
                    </Button>

                    <Button
                      onClick={saveDraft}
                      variant="outlined"
                      fullWidth={{ xs: true, sm: false }}
                    >
                      Save Draft
                    </Button>

                    <Button
                      onClick={handleNext}
                      variant="contained"
                      endIcon={<ArrowForward />}
                      fullWidth={{ xs: true, sm: false }}
                      disabled={uploadingImages || continueDisabled}
                      sx={{
                        ml: { sm: "auto" }, // only push on desktop
                        background: "linear-gradient(90deg,#06b6d4,#7c3aed)",
                        color: "white",
                      }}
                    >
                      Continue
                    </Button>
                  </Box>

                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ mb: 1 }}>Why a video?</Typography>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: "background.default", boxShadow: 1 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>Benefits</Typography>
                    <ul style={{ marginTop: 0 }}>
                      <li>Shows the flow and layout better than photos.</li>
                      <li>Buyers feel more confident with a short walkthrough.</li>
                      <li>Keep video under 60 seconds for faster upload.</li>
                    </ul>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Success */}
          {activeStep === 4 && (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CheckCircle sx={{ fontSize: 80, color: "green", mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>Success!</Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>🎉 "{savedPropertyName}" has been updated!</Typography>
              <Typography variant="h6" sx={{ fontSize: 16, color: "text.secondary" }}>Updated changes will effect on your customer's page!</Typography>
              <Button onClick={() => {
                navigate("/list-properties")
              }} variant="contained" sx={{ mt: 4, background: 'green' }}>Edit another Property</Button>
            </Box>
          )}

        </Box>
      </Box>

      <Snackbar open={!!successMsg} autoHideDuration={3000} onClose={() => setSuccessMsg("")} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="success" sx={{ width: '100%' }}>{successMsg}</Alert>
      </Snackbar>

      <Snackbar open={!!errorMsg} autoHideDuration={4000} onClose={() => setErrorMsg("")} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="error" sx={{ width: '100%' }}>{errorMsg}</Alert>
      </Snackbar>

      <Dialog open={cancelConfirm} onClose={() => setCancelConfirm(false)}>
        <DialogTitle>Confirm Reset</DialogTitle>
        <DialogContent>Are you sure you want to clear the form? All inputs will be reset.</DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirm(false)}>No</Button>
          <Button color="error" onClick={() => {
            setCancelConfirm(false);
            setProperty(initialProperty);
            setImageFiles([]);
            setImagePreviews([]);
            setVideoFile(null);
            setImagesUploaded(false);
            setVideoUploaded(false);
            clearDraft();
          }}>Yes, Reset</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
